import { MapData, MapId, SceneId } from './api';

/**
 * Helper to get chunk coordinates from world pixel position.
 * @param x - World x position in pixels
 * @param y - World y position in pixels
 * @param tileSize - Tile size in pixels (from map metadata)
 * @param chunkSize - Tiles per chunk (from map metadata)
 */
export function getChunkCoords(
  x: number,
  y: number,
  tileSize: number,
  chunkSize: number,
): { chunkX: number; chunkY: number } {
  const tileX = Math.floor(x / tileSize);
  const tileY = Math.floor(y / tileSize);
  return {
    chunkX: Math.floor(tileX / chunkSize),
    chunkY: Math.floor(tileY / chunkSize),
  };
}

/**
 * Get a string key for a chunk coordinate pair.
 */
export function getChunkKey(chunkX: number, chunkY: number): string {
  return `${chunkX},${chunkY}`;
}

/**
 * Parse a chunk key back into coordinates.
 * @throws Error if key is malformed
 */
export function parseChunkKey(key: string): { chunkX: number; chunkY: number } {
  const parts = key.split(',');
  if (parts.length !== 2) {
    throw new Error(`Invalid chunk key format: "${key}". Expected format "x,y".`);
  }
  const chunkX = Number(parts[0]);
  const chunkY = Number(parts[1]);
  if (!Number.isFinite(chunkX) || !Number.isFinite(chunkY)) {
    throw new Error(`Invalid chunk key values: "${key}". Both x and y must be valid numbers.`);
  }
  return { chunkX, chunkY };
}

/**
 * Gets the tile size for a scene from its map data.
 * Throws if map data is unavailable.
 */
export function getTileSizeForScene(
  sceneId: SceneId,
  scenes: Partial<Record<SceneId, MapId>>,
  maps: Partial<Record<MapId, MapData>>,
): number {
  const mapId = scenes[sceneId];
  if (!mapId) {
    throw new Error(`No map ID found for scene: ${sceneId}`);
  }

  const mapData = maps[mapId];
  if (!mapData || !mapData.tilesets || mapData.tilesets.length === 0) {
    throw new Error(`No map data or tilesets found for map: ${mapId}`);
  }

  return mapData.tilesets[0].tilewidth;
}
